import { toJS } from 'mobx';
import { observer, PropTypes as MobXPropTypes } from 'mobx-react';
import cn from 'classnames';
import React, { Component, Fragment } from 'react';
import b from 'b_';
import PropTypes from 'prop-types';
import { arrayDiff } from '../../diff';
import InputStore from '../../stores/InputStore';
import UrlBulkEditorCardStore from '../../stores/UrlBulkEditorCardStore';
import Card from '../Fuse/Card';
import Table from '../Fuse/Table';
import { FTHeading } from '../Fuse';
import Icon from '../Icon/Icon';
import UrlBulkEditorCard from './UrlBulkEditorCard';
import './StatefulBulkEditor.css';

export const BULK_EDITOR_STATE = {
    EDIT_CONFIRM: 'edit_confirm',
    NORMAL: 'normal',
    READ_ONLY: 'read_only',
    AUDIT: 'audit'
};
const block = b.lock('StatefulBulkEditor');

class StatefulBulkEditor extends Component {
    static propTypes = {
        mode: PropTypes.oneOfType([PropTypes.shape({
            type: PropTypes.oneOf([BULK_EDITOR_STATE.EDIT_CONFIRM]),
            // We use these passed explicitly since the bulk editor has a weird notion of previous patterns.
            // E.g. each time it is re-rendered it resets the previous patterns. That loses changes done when
            // going back and forth between edit confirmation and actual edit
            oldItems: MobXPropTypes.arrayOrObservableArrayOf(PropTypes.string).isRequired,
            newItems: MobXPropTypes.arrayOrObservableArrayOf(PropTypes.string).isRequired,
            // ---------
            addedItemsSearch: InputStore.PropType.isRequired,
            removedItemsSearch: InputStore.PropType.isRequired,
        }), PropTypes.shape({
            type: PropTypes.oneOf([BULK_EDITOR_STATE.NORMAL]),
            required: PropTypes.bool
        }), PropTypes.shape({
            type: PropTypes.oneOf([BULK_EDITOR_STATE.READ_ONLY]),
        }), PropTypes.shape({
            type: PropTypes.oneOf([BULK_EDITOR_STATE.AUDIT]),
            removed: MobXPropTypes.arrayOrObservableArrayOf(PropTypes.string).isRequired,
            added: MobXPropTypes.arrayOrObservableArrayOf(PropTypes.string).isRequired,
            addedItemsSearch: InputStore.PropType.isRequired,
            removedItemsSearch: InputStore.PropType.isRequired,
        })]).isRequired,
        wrapperClassName: PropTypes.string,
        readOnlyCardClassName: PropTypes.string,
        readOnlyHeader: PropTypes.node,
        searchPlaceholder: PropTypes.string,
        entityName: PropTypes.string,
        qa: PropTypes.string,
        ...UrlBulkEditorCard.propTypes,
        store: UrlBulkEditorCardStore.PropType, // remove requirement, store is not used in AUDIT mode
    };

    render() {
        const { wrapperClassName, mode, ...otherProps } = this.props;
        switch (mode.type) {
        case BULK_EDITOR_STATE.EDIT_CONFIRM: return this.renderEditConfirm(mode);
        case BULK_EDITOR_STATE.READ_ONLY: return this.renderReadOnly(otherProps);
        case BULK_EDITOR_STATE.AUDIT: return this.renderDiff(mode);
        default: {
            return <UrlBulkEditorCard
                title={'IP Addresses and URLs'}
                subTitle={'Enter IP addresses and URL patterns below (1 entry per line)'}
                validUrlText={'Entries'}
                helpText={'Syntax Requirements'}
                errorMassage={'Invalid IP address or URL pattern'}
                wrapperClassName={cn(block('BulkEditorWrapper'), wrapperClassName)}
                titleClassName={block('BulkEditorTitle')}
                subtitleClassName={block('BulkEditorSubtitle')}
                warningMessage={`Duplicate ${this.getEntityName()} will be deleted on save`}
                {...otherProps}
            />;
        }
        }
    }

    colCfg = (items) => [{
        name: 'url',
        title: `Entries (${items.length})`,
        render: (item) => item
    }];

    search = (term) => (item) => item.toLowerCase().indexOf(term) > -1

    renderEditConfirm = ({ newItems, oldItems, addedItemsSearch, removedItemsSearch }) => {
        const { added, removed } = arrayDiff(toJS(newItems), toJS(oldItems));
        return this.renderDiff({ added, removed, addedItemsSearch, removedItemsSearch });
    }

    getEntityName = () => this.props.entityName || 'IP Addresses and URLs';

    renderDiff = ({ added, removed, addedItemsSearch, removedItemsSearch }) => {
        const addedItems = added.filter(this.search(addedItemsSearch.getValue()));
        const removedItems = removed.filter(this.search(removedItemsSearch.getValue()));
        const entityName = this.getEntityName();

        return <Fragment>
            {removed && removed.length > 0 ? <Card className={block('TableCard')}>
                <Table
                    header={<FTHeading style={{ display: 'flex', placeItems: 'center' }}>
                        <Icon style={{ color: 'red', margin: '1px 4px 0 -6px', fontSize: 20 }}
                            type="do_disturb_on"
                        />
                        Removed {entityName}
                    </FTHeading>}
                    search={removedItemsSearch}
                    searchPlaceholder={this.props.searchPlaceholder || 'Search ' + entityName}
                    pageSizes={[5, 10, 20, 50]}
                    actions={null}
                    pagination
                    columns={this.colCfg(removedItems)}
                    items={removedItems}
                    qa={this.props.qa ? this.props.qa + '-removed' : null}
                />
            </Card> : null}

            {added && added.length > 0 ? <Card className={block('TableCard')}>
                <Table
                    header={<FTHeading style={{ display: 'flex', placeItems: 'center' }}>
                        <Icon style={{ color: '#4CAF50', margin: '1px 4px 0 -6px', fontSize: 20 }}
                            type="add_circle"
                        />
                        Added {entityName}
                    </FTHeading>}
                    search={addedItemsSearch}
                    searchPlaceholder={this.props.searchPlaceholder || 'Search ' + entityName}
                    pageSizes={[5, 10, 20, 50]}
                    actions={null}
                    pagination
                    columns={this.colCfg(addedItems)}
                    items={addedItems}
                    qa={this.props.qa ? this.props.qa + '-added' : null}
                />

            </Card> : null}
        </Fragment>;
    }

    renderReadOnly({ store }) {
        const searchInput = store.urlBulkEditorStore.fuseSearchTextStore.inputStore;
        const searchQuery = searchInput.value ? searchInput.value.trim().toLowerCase() : '';
        const items = !searchQuery ?
            store.getValue() : store.getValue().filter((item) => item.toLowerCase().includes(searchQuery));
        const entityName = this.props.entityName || 'IP Addresses and URLs';
        return <Card className={cn(block('TableCard'), this.props.readOnlyCardClassName)}>
            <Table
                header={
                    this.props.readOnlyHeader ||
                    <FTHeading> {this.props.entityName || 'IP Addresses and URLs'} </FTHeading>
                }
                search={searchInput}
                pageSizes={[5, 10, 20, 50]}
                actions={null}
                pagination
                columns={this.colCfg(items)}
                items={items}
                qa={this.props.qa ? (this.props.qa + '-readonly') : null}
                searchPlaceholder={this.props.searchPlaceholder || 'Search ' + entityName}
            />
        </Card>;
    }
}

export default observer(StatefulBulkEditor);
